/**
 * Owns the title-level bulk schedule section so row behavior and modal state
 * management remain independently focused.
 */
import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { formatUtcDateTimeWithZone } from 'src/utils/formatDateTime';
import type {
  BulkScheduleGroup,
  Schedule,
} from 'src/utils/groupAssetsForBulkSchedule';
import { BulkScheduleGroupRow } from './BulkScheduleGroupRow';

export interface BulkScheduleTitleSection {
  readonly eventEnd?: string;
  readonly eventStart?: string;
  readonly groups: readonly BulkScheduleGroup[];
  readonly gti: string;
  readonly titleName: string;
}

interface BulkScheduleTitleSectionProps {
  readonly editorSchedules: ReadonlyMap<string, Schedule>;
  readonly expandedGroupIds: ReadonlySet<string>;
  readonly section: BulkScheduleTitleSection;
  readonly onChange: (groupId: string, field: keyof Schedule, value: string) => void;
  readonly onReset: (group: BulkScheduleGroup) => void;
  readonly onToggle: (groupId: string) => void;
}

export const BulkScheduleTitleSectionView: React.FC<
BulkScheduleTitleSectionProps
> = ({
  editorSchedules,
  expandedGroupIds,
  section,
  onChange,
  onReset,
  onToggle,
}): React.ReactElement => {
  const { t } = useTranslation();
  const fallback = t('asset_schedule_bulk_event_time_unavailable');
  const titleId = `bulk-schedule-title-${section.gti}`;

  return (
    <Box
      aria-labelledby={titleId}
      data-testid={`bulk-schedule-title-section-${section.gti}`}
      spacingInset="400"
      width="100%"
    >
      <Column spacing="300">
        <Text id={titleId} type="h100">{section.titleName}</Text>
        <Row spacing="500" wrap="down">
          <Column spacing="100">
            <Text color="secondary" type="b100">
              {t('asset_schedule_bulk_event_start')}
            </Text>
            <Text type="b200">
              {formatUtcDateTimeWithZone(section.eventStart, fallback)}
            </Text>
          </Column>
          <Column spacing="100">
            <Text color="secondary" type="b100">
              {t('asset_schedule_bulk_event_end')}
            </Text>
            <Text type="b200">
              {formatUtcDateTimeWithZone(section.eventEnd, fallback)}
            </Text>
          </Column>
        </Row>
        {section.groups.map((group) => {
          const schedule = editorSchedules.get(group.groupId);
          if (!schedule) return null;
          return (
            <BulkScheduleGroupRow
              group={group}
              isExpanded={expandedGroupIds.has(group.groupId)}
              key={group.groupId}
              schedule={schedule}
              onChange={(field, value) => {
                onChange(group.groupId, field, value);
              }}
              onReset={() => { onReset(group); }}
              onToggle={() => { onToggle(group.groupId); }}
            />
          );
        })}
      </Column>
    </Box>
  );
};
